
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { PostureAnalysis, Client, Assessment, View as CaptureSequence, AnalysisResult } from './types';
import { drawLandmarks, drawPlumb, drawViewportOrientationLabels, drawCustomPelvis } from '@/components/posture-analysis/lib/drawing';
import { analyzeAnterior, analyzePosterior, analyzeLateral } from '@/components/posture-analysis/lib/analysis';
import { rotateLandmarks, rotateCustomLandmarks } from '@/lib/posture-analysis/floor-correction';
import { filterMetricsByExperimental } from '@/components/posture-analysis/lib/posture-metrics-filter';
import { getSopPdfExportRows, resolveAssessmentTestMode } from '@/components/sop-intake/course-playlist-config';
import { getAppSettings } from '@/components/settings/lib/settings-context';
import { formatDateSafe, formatDateLocale } from './utils';
import type { TestLateralization } from '@/components/sop-intake/sop-analyzer/types';
import { postAnalyzeSop } from '@/lib/evaluation-api-client';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import {
  applyPdfBrandFonts,
  PDF_TABLE_FONT_STYLES,
  PDF_TABLE_HEAD_FONT_STYLES,
  setPdfBodyFont,
  setPdfHeadingFont,
} from '@/lib/pdf-brand-fonts';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';


const captureSequence: CaptureSequence[] = ['Anterior', 'Posterior', 'Left', 'Right'];

const PDF_COLOR_FG: [number, number, number] = [22, 30, 42];
const PDF_COLOR_MUTED: [number, number, number] = [89, 89, 89];

const postureTableDefaults = {
  styles: PDF_TABLE_FONT_STYLES,
  headStyles: { fillColor: [41, 128, 185], ...PDF_TABLE_HEAD_FONT_STYLES },
};

/**
 * The identity line uses `clientDisplayLabel`, the same choke point every
 * other client-identity render goes through. Shared by every PDF export that
 * calls `addHeader` (posture analysis, SOP assessment).
 */
const addHeader = (doc: jsPDF, title: string, client: Client | null, date: Date | unknown) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = margin;

  setPdfHeadingFont(doc);
  doc.setFontSize(24);
  doc.setTextColor(...PDF_COLOR_FG);
  doc.text(title, margin, y + 10);

  y += 20;

  setPdfBodyFont(doc, 'normal');
  doc.setFontSize(12);
  doc.setTextColor(...PDF_COLOR_MUTED);
  if (client) {
    doc.text(`Client: ${clientDisplayLabel(client)}`, margin, y);
    y += 6;
  }

  doc.text(`Date: ${formatDateLocale(date)}`, margin, y);

  y += 10;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, pageWidth - margin, y);

  return y + 15;
};

const writeSectionHeading = (doc: jsPDF, margin: number, y: number, text: string, size = 14): number => {
  setPdfHeadingFont(doc);
  doc.setFontSize(size);
  doc.setTextColor(...PDF_COLOR_FG);
  doc.text(text, margin, y);
  return y + (size >= 16 ? 8 : 6);
};


function getDisplayMetricLabel(view: CaptureSequence, label: string): string {
  if ((view === 'Left' || view === 'Right') && label.includes('Craniovertebral')) {
    return 'CV Angle (Head fwd)';
  }
  return label;
}

function translateFormattedValue(formattedValue: string): string {
  if (!formattedValue) return formattedValue;
  return formattedValue
    .replace(/\bPost\b/g, 'Back')
    .replace(/\bAnt\b/g, 'Front')
    .replace(/\bPosterior\b/g, 'Back')
    .replace(/\bAnterior\b/g, 'Front');
}

export interface AppendPostureAnalysisPdfOptions {
  title?: string;
  laymanMode?: boolean;
  /** When true, always begin this section on a new page (required when appending after html2canvas content). */
  startOnNewPage?: boolean;
}

function loadPdfImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const checkComplete = () => {
      if (img.complete && (img.naturalWidth > 0 || img.width > 0)) {
        resolve(img);
        return true;
      }
      return false;
    };

    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;

    if (checkComplete()) return;

    setTimeout(() => {
      if (checkComplete()) return;
      if (img.naturalWidth > 0 || img.width > 0) {
        resolve(img);
      } else {
        reject(new Error('Image load timed out'));
      }
    }, 5000);
  });
}

export const appendPostureAnalysisToPdf = async (
  doc: jsPDF,
  analysis: PostureAnalysis,
  client: Client | null,
  options?: AppendPostureAnalysisPdfOptions,
): Promise<jsPDF> => {
  const margin = 15;
  const pageWidth = doc.internal.pageSize.getWidth();
  const appSettings = getAppSettings();
  const experimentalEnabled = appSettings.experimentalFeaturesEnabled === true;
  const title = options?.title ?? 'Posture Analysis Report';

  const viewsToExport = experimentalEnabled ? captureSequence : captureSequence.filter(v => v !== 'Posterior');
  const hasContent = viewsToExport.some(view => analysis.captured[view as CaptureSequence]);

  if (!hasContent) {
    throw new Error('No analysis content available to export.');
  }

  if (options?.startOnNewPage) {
    doc.addPage();
  }

  await applyPdfBrandFonts(doc);

  let y = addHeader(doc, title, client, analysis.createdAt);

  const reports: Partial<Record<CaptureSequence, AnalysisResult[]>> = {};
  for (const view of viewsToExport) {
    const capturedImage = analysis.captured[view as CaptureSequence];
    if (!capturedImage?.landmarks || capturedImage.cmPerPx === undefined) continue;
    const floorCorrectionDeg = capturedImage.floorCorrectionDeg ?? 0;
    const floorCorrectionPivot = capturedImage.floorCorrectionPivot || undefined;
    const rotatedCustom = rotateCustomLandmarks(capturedImage.custom, floorCorrectionDeg, floorCorrectionPivot);
    const rotatedLm = rotateLandmarks(capturedImage.landmarks, floorCorrectionDeg, floorCorrectionPivot);
    if (view === 'Anterior') {
      reports[view] = analyzeAnterior(rotatedLm, view, capturedImage.cmPerPx, analysis.unit, rotatedCustom);
    } else if (view === 'Posterior') {
      reports[view] = analyzePosterior(rotatedLm, view, capturedImage.cmPerPx, analysis.unit, rotatedCustom);
    } else if (view === 'Left' || view === 'Right') {
      reports[view] = analyzeLateral(rotatedLm, view, capturedImage.cmPerPx, analysis.unit, rotatedCustom, {
        omitPelvicTilt: !!capturedImage.pelvisPinsOptOut,
      });
    }
  }

  for (const view of viewsToExport) {
    const capturedImage = analysis.captured[view as CaptureSequence];
    if (!capturedImage?.imageData || !capturedImage.landmarks || capturedImage.cmPerPx === undefined) continue;

    const colWidth = (pageWidth - margin * 3) / 2;
    let img: HTMLImageElement;
    try {
      img = await loadPdfImage(capturedImage.imageData);
    } catch {
      continue;
    }

    const realWidth = img.naturalWidth || img.width || 600;
    const realHeight = img.naturalHeight || img.height || 800;
    const imgHeight = (realHeight * colWidth) / realWidth;

    let tableBody: [string, string, number][] = [];
    let detailedReport: AnalysisResult[] = reports[view as CaptureSequence] ?? [];

    const processedData = filterMetricsByExperimental([...detailedReport], experimentalEnabled);
    let combinedCalcaneus = null as null | { left: AnalysisResult; right: AnalysisResult };

    if (view === 'Posterior') {
      const calcLIndex = processedData.findIndex(d => d.label.toLowerCase() === 'calcaneus tilt (l)');
      const calcRIndex = processedData.findIndex(d => d.label.toLowerCase() === 'calcaneus tilt (r)');
      if (calcLIndex !== -1 && calcRIndex !== -1) {
        combinedCalcaneus = { left: processedData[calcLIndex], right: processedData[calcRIndex] };
      }
    }

    const filteredData = view === 'Posterior' && combinedCalcaneus
      ? processedData.filter(d => d.label.toLowerCase() !== 'calcaneus tilt (l)' && d.label.toLowerCase() !== 'calcaneus tilt (r)')
      : processedData;

    tableBody = filteredData.map(item => {
      const displayLabel = getDisplayMetricLabel(view, item.label);
      const displayValue = options?.laymanMode ? translateFormattedValue(item.formattedValue) : item.formattedValue;
      return [displayLabel, displayValue, item.sev];
    });

    if (combinedCalcaneus) {
      const leftVal = options?.laymanMode ? translateFormattedValue(combinedCalcaneus.left.formattedValue) : combinedCalcaneus.left.formattedValue;
      const rightVal = options?.laymanMode ? translateFormattedValue(combinedCalcaneus.right.formattedValue) : combinedCalcaneus.right.formattedValue;
      tableBody.push([
        'Calcaneus Tilt (Left)',
        leftVal,
        combinedCalcaneus.left.sev
      ]);
      tableBody.push([
        'Calcaneus Tilt (Right)',
        rightVal,
        combinedCalcaneus.right.sev
      ]);
    }

    const estimatedTableHeight = (tableBody.length + 2) * 10;
    const sectionHeight = Math.max(imgHeight, estimatedTableHeight) + 20;

    if (y + sectionHeight > doc.internal.pageSize.getHeight()) {
      doc.addPage();
      y = margin;
    }

    setPdfHeadingFont(doc);
    doc.setFontSize(16);
    doc.setTextColor(...PDF_COLOR_FG);
    doc.text(`${view} View`, margin, y);
    y += 8;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = realWidth;
    tempCanvas.height = realHeight;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) continue;

    tempCtx.drawImage(img, 0, 0, realWidth, realHeight);

    drawPlumb(tempCtx, view, capturedImage.landmarks);
    drawLandmarks(tempCtx, capturedImage.landmarks, undefined, undefined, view);
    drawCustomPelvis(tempCtx, view, capturedImage.custom);
    drawViewportOrientationLabels(tempCtx, view, { width: realWidth, height: realHeight }, options?.laymanMode);

    const imageDataUrl = tempCanvas.toDataURL('image/jpeg', 0.85);
    doc.addImage(imageDataUrl, 'JPEG', margin, y, colWidth, imgHeight);

    if (tableBody.length > 0) {
      autoTable(doc, {
        head: [['Metric', 'Value', 'Severity']],
        body: tableBody,
        startY: y,
        margin: { left: margin + colWidth + 5 },
        tableWidth: colWidth - 5,
        theme: 'striped',
        ...postureTableDefaults,
        didParseCell: (data: any) => {
          if (data.column.dataKey === 2) {
            const severity = data.cell.raw;
            let color = [255, 255, 255]; // Default white
            if (severity === 1) color = [253, 224, 71]; // yellow-300
            if (severity === 2) color = [251, 146, 60]; // orange-400
            if (severity === 3) color = [239, 68, 68]; // red-500
            if (severity > 0) {
              data.cell.styles.fillColor = color;
              data.cell.styles.textColor = [0, 0, 0];
            }
            data.cell.text = ['OK', 'Minor', 'Moderate', 'Severe'][severity] || "N/A";
          }
        },
        columnStyles: {
          0: { cellWidth: 'auto' }, // Metric name
          1: { cellWidth: 25 }, // Value
          2: { cellWidth: 25 }  // Severity
        }
      });
      const tableFinalY = (doc as any).lastAutoTable.finalY;
      y = Math.max(y + imgHeight, tableFinalY) + 15;
    } else {
      y = y + imgHeight + 15;
    }
  }

  return doc;
};

export const exportPostureAnalysisToPdf = async (analysis: PostureAnalysis, client: Client | null) => {
  const doc = new jsPDF();
  await appendPostureAnalysisToPdf(doc, analysis, client);
  const exportName = `${(client ? clientDisplayLabel(client) : '').replace(/\s/g, '_') || 'Unnamed'}_Posture_Analysis_${formatDateSafe(analysis.createdAt, 'yyyy-MM-dd')}.pdf`;
  doc.save(exportName);
};



const formatGrounding = (assessment: Assessment, side: 'L' | 'R'): string => {
  const candidates = [
    assessment[`${side}_GroundingPerformance_actual`],
    assessment[`${side}_GroundingPerformance_perceived`],
    assessment[`${side}_GroundingPerformance`],
  ];
  const value = candidates.find((item) => item !== null && item !== undefined && String(item).trim() !== '');
  return value ? String(value) : 'N/A';
};

const bucketTests = (tests: TestLateralization[]) => ({
  left: tests.filter((test) => test.bias === 'Left'),
  balanced: tests.filter((test) => test.bias === 'Even' || test.bias === null),
  right: tests.filter((test) => test.bias === 'Right'),
});

const formatTestBadge = (test: TestLateralization): string => {
  const flags: string[] = [];
  if (test.isHypermobile) flags.push('Hyper');
  if (test.isInvalidated) flags.push('Invalidated');
  return flags.length > 0 ? `${test.testLabel} (${flags.join(', ')})` : test.testLabel;
};


export const exportSopAnalysisToPdf = async (
  assessment: Assessment,
  client: Client | null,
  version: RuleDocVersion = 'current'
) => {
  const doc = new jsPDF();
  const margin = 15;
  const pageWidth = doc.internal.pageSize.getWidth();
  const postureClassification = client?.postureAnalyses?.[0]?.classification?.pattern;
  const assessmentRecord = assessment as Record<string, unknown>;
  const testMode = resolveAssessmentTestMode(assessmentRecord);
  const analysisResult = await postAnalyzeSop({
    formValues: assessment as any,
    ruleDocVersion: version,
    postureClassification: postureClassification ?? null
  });

  await applyPdfBrandFonts(doc);

  let y = addHeader(doc, "SOP Assessment Report", client, assessment.createdAt ?? assessment.date);

  setPdfBodyFont(doc, 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...PDF_COLOR_MUTED);
  doc.text(`Assessment mode: ${testMode === 'active' ? 'Active (remote)' : 'Passive (in-person)'}`, margin, y);
  y += 10;

  y = writeSectionHeading(doc, margin, y, "Detailed Assessment Values");

  const exportRows = getSopPdfExportRows(assessmentRecord, version, {
    mode: testMode,
    footBatteryEnabled: false,
    showOptionalAssessments: true,
  });
  const tableBody = exportRows.map((row) => [row.label, row.left, row.right, row.goal]);

  autoTable(doc, {
    head: [['Test', 'Left Value', 'Right Value', 'Goal']],
    body: tableBody,
    startY: y,
    theme: 'striped',
    ...postureTableDefaults,
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 25 },
      2: { cellWidth: 25 },
      3: { cellWidth: 30 }
    }
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  if (testMode === 'passive') {
    y = writeSectionHeading(doc, margin, y, "Qualitative Analysis");
    const qualitativeRows: [string, string][] = [
      ['Grounding (L)', formatGrounding(assessment, 'L')],
      ['Grounding (R)', formatGrounding(assessment, 'R')],
    ];
    if (assessment.FootPresentation) {
      qualitativeRows.splice(1, 0, ['Foot Presentation', String(assessment.FootPresentation)]);
    }
    autoTable(doc, {
      body: qualitativeRows,
      startY: y,
      theme: 'plain',
      styles: PDF_TABLE_FONT_STYLES,
    });
    y = (doc as any).lastAutoTable.finalY + 15;
  }

  y = writeSectionHeading(doc, margin, y, "Analysis Summary");
  setPdfBodyFont(doc, 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...PDF_COLOR_MUTED);

  const maxWidth = pageWidth - (margin * 2);
  const splitText = doc.splitTextToSize(analysisResult.displaySummary, maxWidth);
  doc.text(splitText, margin, y);
  y += (splitText.length * 6) + 10;

  y = writeSectionHeading(doc, margin, y, "Orientation Findings");
  const orientationNotes = [
    ...analysisResult.orientation.flags,
    ...analysisResult.orientation.hypermobilityFlags,
  ];
  autoTable(doc, {
    head: [['Notes']],
    body: (orientationNotes.length > 0 ? orientationNotes : ['No orientation flags detected']).map((note) => [note]),
    startY: y,
    theme: 'striped',
    styles: PDF_TABLE_FONT_STYLES,
    headStyles: { fillColor: [52, 73, 94], ...PDF_TABLE_HEAD_FONT_STYLES },
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  y = writeSectionHeading(doc, margin, y, "Lateralization by Region");
  const sectionRows = Object.entries(analysisResult.lateralization.sectionSummaries).map(([section, summary]) => [
    section,
    summary,
  ]);
  autoTable(doc, {
    head: [['Region', 'Bias']],
    body: sectionRows,
    startY: y,
    theme: 'striped',
    ...postureTableDefaults,
  });
  y = (doc as any).lastAutoTable.finalY + 12;

  y = writeSectionHeading(doc, margin, y, "Lateralized Test Buckets");
  y += 2;

  const buckets = bucketTests(analysisResult.lateralization.tests);
  const maxRows = Math.max(
    buckets.left.length || 1,
    buckets.balanced.length || 1,
    buckets.right.length || 1
  );

  const analysisTableBody: Array<Array<{ content: string; styles?: Record<string, unknown> }>> = [];
  for (let i = 0; i < maxRows; i++) {
    const leftItem = buckets.left[i];
    const balancedItem = buckets.balanced[i];
    const rightItem = buckets.right[i];

    const formatCell = (item: TestLateralization | undefined) => {
      if (!item) return { content: '', styles: { fillColor: [255, 255, 255] } };
      return {
        content: formatTestBadge(item),
        styles: {
          fillColor: item.isInvalidated ? [255, 220, 220] : [220, 255, 220],
        }
      };
    };

    analysisTableBody.push([
      formatCell(leftItem),
      formatCell(balancedItem),
      formatCell(rightItem)
    ]);
  }

  if (analysisTableBody.length === 0 || (buckets.left.length === 0 && buckets.balanced.length === 0 && buckets.right.length === 0)) {
    analysisTableBody.push([
      { content: 'None', styles: { halign: 'center' as const } },
      { content: 'None', styles: { halign: 'center' as const } },
      { content: 'None', styles: { halign: 'center' as const } }
    ]);
  }

  autoTable(doc, {
    head: [[
      { content: 'Left-Lateralized', styles: { fillColor: [59, 131, 219], textColor: [255, 255, 255], halign: 'center' as const } },
      { content: 'Balanced', styles: { fillColor: [46, 204, 113], textColor: [255, 255, 255], halign: 'center' as const } },
      { content: 'Right-Lateralized', styles: { fillColor: [231, 76, 60], textColor: [255, 255, 255], halign: 'center' as const } }
    ]],
    body: analysisTableBody as (string | number | { content: string; styles?: Record<string, unknown> })[][],
    startY: y,
    theme: 'grid',
    styles: PDF_TABLE_FONT_STYLES,
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 'auto' }
    }
  });

  const exportName = `${(client ? clientDisplayLabel(client) : '').replace(/\s/g, '_') || 'Unnamed'}_SOP_Analysis_${formatDateSafe(assessment.createdAt ?? assessment.date, "yyyy-MM-dd")}.pdf`;
  doc.save(exportName);
};


